import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ListTableShell } from "@/components/ResourceShell";
import { ListToolbar } from "@/components/ListToolbar";
import { CellStatusBadge } from "@/components/StatusBadges";
import { OneTimeTokenDialog } from "@/components/OneTimeTokenDialog";
import { CreateCellDialog } from "@/components/dialogs/CreateCellDialog";
import { useCells } from "@/api/queries/provisioning";
import { relativeTime } from "@/lib/relativeTime";

// #343 UC-1: Zellen-Register. Das Enroll-Token erscheint einmalig (OneTimeTokenDialog) mit dem meshctl-Aufruf.
export function enrollHint(cellName: string): string {
  return `MESH_ENROLL_TOKEN=<token> deploy/k8s/meshctl.sh cell-enroll ${cellName}`;
}

export function CellsList() {
  const cells = useCells();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [token, setToken] = useState<{ cell: string; token: string } | null>(null);

  const all = cells.data ?? [];
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return term ? all.filter((c) => c.name.includes(term) || c.provider.includes(term)) : all;
  }, [all, q]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-text3">{filtered.length} von {all.length} Zellen</div>
        <Button onClick={() => setCreateOpen(true)} data-testid="create-cell-button"><Plus className="h-4 w-4" /> Zelle anlegen</Button>
      </div>
      <ListToolbar search={q} onSearch={setQ} placeholder="Zelle suchen…" />
      <ListTableShell isLoading={cells.isLoading} isError={cells.isError} errorMessage={cells.error?.message} isEmpty={filtered.length === 0} emptyMessage={all.length === 0 ? "Noch keine Zellen" : "Keine Treffer"} emptyHint="Eine Zelle = ein Kubernetes-Cluster mit Provisioner-Agent." onRetry={() => cells.refetch()}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Provider</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Zuletzt gesehen</TableHead>
              <TableHead>Enroll-Token</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((c) => (
              <TableRow key={c.id} data-clickable="true" onClick={() => navigate(`/cells/${c.id}`)}>
                <TableCell className="font-medium">{c.name}</TableCell>
                <TableCell className="text-text2">{c.provider}</TableCell>
                <TableCell><CellStatusBadge status={c.status} /></TableCell>
                <TableCell className="text-text3">{c.lastSeenAt ? relativeTime(c.lastSeenAt) : "—"}</TableCell>
                <TableCell className="text-text3">{c.hasOpenEnrollToken ? `offen bis ${c.enrollTokenExpiresAt?.slice(0, 10)}` : "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ListTableShell>
      {createOpen && <CreateCellDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={(cell, t) => setToken({ cell, token: t })} />}
      {token && (
        <OneTimeTokenDialog
          open={!!token}
          onOpenChange={(o) => !o && setToken(null)}
          title={`Enroll-Token fuer ${token.cell}`}
          description={<span>In der Zelle: <code className="font-mono text-xs">{enrollHint(token.cell)}</code> — 7 Tage gueltig, einmal verwendbar.</span>}
          label="Enroll-Token"
          token={token.token}
        />
      )}
    </div>
  );
}
